/*
 * Enova — Elementos de conversión de la ficha de producto
 * =======================================================
 * <enova-express>      cuenta regresiva al horario de corte del envío express.
 *                      Se repite cada día y salta los días sin despacho.
 * <enova-pickup-card>  tarjeta que abre el drawer nativo de retiro en tienda de
 *                      Shopify y se oculta sola si la variante no tiene retiro.
 */

(function () {
  const DAY = 86400000;

  /* ── Envío express ─────────────────────────────────────────────────────── */

  if (!customElements.get('enova-express')) {
    customElements.define(
      'enova-express',

      class EnovaExpress extends HTMLElement {
        connectedCallback() {
          this.hours = this.querySelector('[data-cd-hours]');
          this.minutes = this.querySelector('[data-cd-minutes]');
          this.seconds = this.querySelector('[data-cd-seconds]');
          this.note = this.querySelector('[data-cd-note]');
          this.a11y = this.querySelector('[data-cd-a11y]');

          this.cutoff = this.parseCutoff(this.getAttribute('data-cutoff'));
          if (!this.cutoff) return;

          this.offsetMs = this.parseOffset(this.getAttribute('data-utc-offset'));
          this.offDays = (this.getAttribute('data-off-days') || '')
            .split(',')
            .filter((value) => value !== '')
            .map(Number);
          this.locale = this.getAttribute('data-locale') || 'es';
          this.noteTemplate = this.getAttribute('data-note-template') || '';

          // Los siete días marcados como sin despacho dejarían el reloj sin destino.
          if (this.offDays.length >= 7) return;

          this.lastAnnounced = null;
          this.tick();
          this.timer = setInterval(() => this.tick(), 1000);
        }

        disconnectedCallback() {
          clearInterval(this.timer);
        }

        parseCutoff(value) {
          const match = /^(\d{1,2}):(\d{1,2})$/.exec((value || '').trim());
          if (!match) return null;

          const hours = Number(match[1]);
          const minutes = Number(match[2]);
          if (hours > 23 || minutes > 59) return null;

          return {hours, minutes};
        }

        // '-0500' → -18000000 ms
        parseOffset(value) {
          const match = /^([+-])(\d{2})(\d{2})$/.exec((value || '').trim());
          if (!match) return 0;

          const sign = match[1] === '-' ? -1 : 1;

          return sign * (Number(match[2]) * 3600000 + Number(match[3]) * 60000);
        }

        /* `at` es el corte expresado como reloj de pared de la tienda dentro de
           un timestamp UTC, así que hay que formatearlo en UTC para no correrlo
           un día. */
        weekdayName(at) {
          try {
            return new Date(at).toLocaleDateString(this.locale, {weekday: 'long', timeZone: 'UTC'});
          } catch (error) {
            return '';
          }
        }

        /* Reloj de pared de la tienda: desplazamos el instante actual por el huso
           horario y luego leemos los getters UTC. */
        shopNow() {
          return new Date(Date.now() + this.offsetMs);
        }

        nextCutoff() {
          const now = this.shopNow();
          const nowMs = now.getTime();
          const midnight = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());

          for (let i = 0; i <= 14; i++) {
            const dayStart = midnight + i * DAY;
            const weekday = new Date(dayStart).getUTCDay();
            if (this.offDays.indexOf(weekday) !== -1) continue;

            const at = dayStart + this.cutoff.hours * 3600000 + this.cutoff.minutes * 60000;
            if (at > nowMs) return {at, weekday, isToday: i === 0};
          }

          return null;
        }

        tick() {
          const target = this.nextCutoff();
          if (!target) return;

          const remaining = Math.max(0, target.at - this.shopNow().getTime());
          const totalSeconds = Math.floor(remaining / 1000);
          const hours = Math.floor(totalSeconds / 3600);
          const minutes = Math.floor((totalSeconds % 3600) / 60);
          const seconds = totalSeconds % 60;

          this.hours.textContent = this.pad(hours);
          this.minutes.textContent = this.pad(minutes);
          this.seconds.textContent = this.pad(seconds);

          this.renderNote(target);
          this.announce(hours, minutes);
        }

        pad(value) {
          return value < 10 ? '0' + value : String(value);
        }

        renderNote(target) {
          if (!this.note) return;

          const day = target.isToday ? '' : this.weekdayName(target.at);

          if (day && this.noteTemplate) {
            this.note.textContent = this.noteTemplate.replace('{dia}', day);
            this.note.hidden = false;
          } else {
            this.note.hidden = true;
          }
        }

        // Se anuncia por minuto, no por segundo, para no saturar al lector de pantalla.
        announce(hours, minutes) {
          if (!this.a11y) return;

          const stamp = hours + ':' + minutes;
          if (stamp === this.lastAnnounced) return;

          this.lastAnnounced = stamp;
          this.a11y.textContent = `Quedan ${hours} horas y ${minutes} minutos para el cierre del envío express.`;
        }
      }
    );
  }

  /* ── Paneles informativos: no abrir sin pedido del usuario ─────────────── */

  /* El popup-component del tema abre TODOS los diálogos de la página cuando la
     URL trae accepts_marketing o customer_posted=true (el retorno del formulario
     de newsletter). Eso tiene sentido para un popup de marketing, no para estos
     paneles de información de envío, así que los volvemos a cerrar. */
  if (/[?&](accepts_marketing|customer_posted)=/.test(window.location.search)) {
    const closeInfoDrawers = () => {
      document.querySelectorAll('.enova-conv--cards popup-component > dialog[open]').forEach((dialog) => {
        dialog.close();
      });
    };

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', closeInfoDrawers);
    } else {
      closeInfoDrawers();
    }
  }

  /* ── Tarjeta de retiro en tienda ───────────────────────────────────────── */

  if (!customElements.get('enova-pickup-card')) {
    customElements.define(
      'enova-pickup-card',

      class EnovaPickupCard extends HTMLElement {
        connectedCallback() {
          this.button = this.querySelector('[data-conv-pickup]');
          if (!this.button) return;

          // El <pickup-availability> se inyecta en el mismo bloque de tarjetas.
          this.source =
            (this.closest('.enova-conv--cards') || document).querySelector('.enova-conv__pickup-source') ||
            document.querySelector('pickup-availability');

          this.button.addEventListener('click', () => this.open());

          if (!this.source) return;

          this.onMutation = () => this.sync();
          this.observer = new MutationObserver(this.onMutation);
          this.observer.observe(this.source, {childList: true, subtree: true, attributes: true, attributeFilter: ['class']});
          this.sync();
        }

        disconnectedCallback() {
          if (this.observer) this.observer.disconnect();
        }

        trigger() {
          return (
            (this.source && this.source.querySelector('[data-popup-open]')) ||
            document.getElementById('ShowPickupAvailabilityDrawer')
          );
        }

        /* La tarjeta arranca visible y solo se oculta cuando la respuesta de
           Shopify confirma que esta variante no tiene retiro disponible. */
        sync() {
          if (!this.source || !this.source.children.length) return;

          this.hidden = this.source.classList.contains('hidden') || !this.trigger();
        }

        open() {
          const trigger = this.trigger();
          if (trigger) trigger.click();
        }
      }
    );
  }
})();
